/**
 * PHARMAKON — French copy of S1 « Nouvel achat » (/achats/nouveau, /achats/:id/modifier),
 * verbatim from design/ux/02_achats.md §4.19 (message ids M-S1-nn) and the labels of §3.1, §4.2-§4.9
 * (refonte WP-22). One file so the copy stays reviewable; no component types a sentence of its own.
 *
 * Templated messages are functions; every money / quantity argument is already formatted by the
 * caller (config/locale.config.js `fmtMoney`, lib/number-format.js), never here.
 */

/** M-S1-nn — the message table of ux/02 §4.19 (validation, warnings, states). */
export const M_S1 = Object.freeze({
  FOURNISSEUR_INCONNU: "Choisissez un fournisseur dans la liste.", // M-S1-01
  NUMERO_EXISTE: (numero, statut) =>
    `Ce numéro existe déjà pour ce fournisseur (achat ${numero}, ${statut}).`, // M-S1-02
  NUMERO_EXISTE_LIEN: "Ouvrir cet achat",
  DATE_FUTURE: "La date de facture ne peut pas être dans le futur.", // M-S1-03
  RECEPTION_AVANT_FACTURE:
    "La date de réception est antérieure à la date de facture. Vérifiez la saisie.", // M-S1-04
  MAGASIN_REQUIS: "Choisissez le magasin qui reçoit la marchandise.", // M-S1-05
  NOTES_LONGUEUR: (max) => `${max} caractères au maximum.`, // M-S1-07
  BC_SANS_FOURNISSEUR: "Choisissez d'abord un fournisseur.", // M-S1-09
  PRODUIT_REQUIS: "Choisissez un produit dans la liste, scannez sa boîte ou créez-le (Alt+Entrée).", // M-S1-10
  QTE_INVALIDE: (max) => `Saisissez une quantité entière entre 1 et ${max}.`, // M-S1-11
  UG_INVALIDE: (max) => `Saisissez un nombre entier d'unités gratuites (0 à ${max}).`, // M-S1-12
  UG_PCT_INVALIDE: "Le pourcentage d'UG doit être compris entre 0 et 100.", // M-S1-13
  UG_PCT_ARRONDI: (p, q, x, ug) => `${p} % de ${q} = ${x} → ${ug} UG (arrondi inférieur)`, // M-S1-13 hint
  // Refonte WP-63 (A-74, A-99) — « UG incluses »: the % of the paid units, the Qté holding the UG.
  UG_PCT_ARRONDI_INCLUSES: (p, q, x, ug) =>
    `${p} % des unités payées sur ${q} (UG incluses) = ${x} → ${ug} UG (arrondi inférieur)`, // M-S1-13 hint
  UG_SANS_UNITE_PAYEE:
    "La Qté inclut les UG : elle doit dépasser les UG d'au moins une unité payée.", // M-S1-12 (WP-63, A-74)
  LOT_PSYCHOTROPE: "Numéro de lot obligatoire pour un psychotrope.", // M-S1-14
  PEREMPTION_REQUISE: "Date de péremption obligatoire pour ce type de produit.", // M-S1-15
  PEREMPTION_PASSEE: "Cette date est déjà passée.", // M-S1-15
  PU_INVALIDE: (max) => `Saisissez un prix entre 0,00 et ${max} DA.`, // M-S1-16
  PU_ZERO: "PU HT à 0 : s'il s'agit d'unités gratuites, saisissez-les dans UG.", // M-S1-17 (avertissement)
  // M-S1-16b — refonte WP-65 (D-27, A-105): where the suggested PU HT comes from (the band's notes line, after « PU HT »); the
  // amount is named only when the field holds another value (a typed one). `date` already formatted.
  PU_SUGGERE_FOURNISSEUR: (date, montant) =>
    `Dernier prix chez ce fournisseur${montant ? ` : ${montant}` : ""}${date ? ` (${date})` : ""}`,
  PU_SUGGERE_AUTRE: (nom, date, montant) =>
    `${nom ? `Dernier prix chez ${nom}` : "Dernier prix connu"}${montant ? ` : ${montant}` : ""}${date ? ` (${date})` : ""}`,
  PU_SUGGERE_AUCUN: (mois) => `Aucun achat de ce produit sur ${mois} mois : saisissez le PU HT.`,
  // M-S1-20b — refonte WP-65 (A-105): the PPA from the latest purchase (the product has none), or the
  // fiche / latest-purchase PPA beside a typed one.
  PPA_SUGGERE_ACHAT: (date, montant) => `PPA du dernier achat${montant ? ` : ${montant}` : ""}${date ? ` (${date})` : ""}`,
  PPA_SUGGERE_PRODUIT: (montant) => `PPA de la fiche produit : ${montant}`,
  // Refonte WP-69 (D-27) — the same notes for a non-médicament line, whose PPA field reads « Prix de vente ».
  PV_SUGGERE_ACHAT: (date, montant) => `Prix de vente du dernier achat${montant ? ` : ${montant}` : ""}${date ? ` (${date})` : ""}`,
  PV_SUGGERE_PRODUIT: (montant) => `Prix de vente de la fiche produit : ${montant}`,
  // M-S1-20c — refonte WP-69 (D-27, A-120): the field the margin computed, said on the notes line after its
  // label; the type's default margin while nothing is computed from it yet.
  PU_CALCULE: "Calculé : prix de vente ÷ (1 + marge)",
  PV_CALCULE: "Calculé : PU HT × (1 + marge)",
  MARGE_CALCULEE: "Calculée : (prix de vente − PU HT) ÷ PU HT",
  MARGE_DU_TYPE: (libelle) => `Marge du type « ${libelle} » (Paramètres › Types de produits)`,
  MARGE_INVALIDE: (min, max) => `Saisissez une marge entre ${min} et ${max} %.`, // M-S1-20d
  RISTOURNE_TROP: (brut) => `La ristourne ne peut pas dépasser le montant de la ligne (${brut}).`, // M-S1-18
  TVA_INVALIDE: (taux) => `Taux de TVA autorisés : ${taux}.`, // M-S1-19
  PPA_INFERIEUR: "Le PPA doit être supérieur ou égal au PU HT (vente à perte interdite).", // M-S1-20
  PPA_INVALIDE: (max) => `Saisissez un PPA entre 0,00 et ${max} DA.`,
  // Refonte WP-69 (D-27) — M-S1-20 for a non-médicament line (the same rule, its own label).
  PV_INFERIEUR: "Le prix de vente doit être supérieur ou égal au PU HT (vente à perte interdite).", // M-S1-20
  PV_INVALIDE: (max) => `Saisissez un prix de vente entre 0,00 et ${max} DA.`,
  // M-S1-21 — refonte WP-63 (D-25, A-75, A-99): Qté reçue = the units that enter the stock.
  RECUE_TROP: (attendue) =>
    `La quantité reçue ne peut pas dépasser ${attendue} (Qté payée + UG). Un surplus se déclare par un avoir « Reçu non facturé ».`,
  RECUE_SOUS_UG: (ug) =>
    `La quantité reçue ne peut pas être inférieure aux UG (${ug}) : si des unités gratuites manquent, corrigez les UG.`,
  RECUE_INVALIDE: "Saisissez un nombre entier d'unités reçues.",
  RECUE_ECART: (recue, attendue, manquant) =>
    `Écart de réception : ${recue} reçue${Number(recue) > 1 ? "s" : ""} sur ${attendue} attendue${Number(attendue) > 1 ? "s" : ""} — ${manquant} unité${manquant > 1 ? "s" : ""} payée${manquant > 1 ? "s" : ""} manquante${manquant > 1 ? "s" : ""}.`,
  TR_INFO: "Tarif de référence du produit. Il se modifie dans la fiche produit.", // M-S1-22
  LIMITE_LIGNES: (max) =>
    `Maximum ${max} lignes par facture atteint. Créez une seconde facture pour la suite.`, // M-S1-23
  REMISE_TROP: "La remise globale ne peut pas dépasser le total HT.", // M-S1-24
  TOTAL_INVALIDE: "Saisissez un montant positif avec au plus deux décimales.", // M-S1-25
  PSYCHOTROPE_MELANGE:
    "Une facture d'achat ne peut pas mélanger des psychotropes et des produits standards. Créez deux factures distinctes.", // M-S1-30
  SCAN_INCONNU: (code) => `Aucun produit avec le code-barre ${code}.`, // M-S1-32
  SCAN_INCONNU_CREER: "Créer le produit — Alt+Entrée", // M-S1-32 action (verbatim)
  SCAN_INCONNU_CREER_BOUTON: "Créer le produit", // M-S1-32 action, the key chip shows « Alt+Entrée »
  SCAN_INCONNU_RECHERCHER: "Rechercher par nom", // M-S1-32 action
  SCAN_LOT: "Ce code est une étiquette de lot Pharmakon, pas le code-barre de la boîte.", // M-S1-33
  SCAN_LIGNE_EN_COURS:
    "Terminez la ligne en cours (Entrée) ou annulez-la (Échap) avant de scanner un autre produit.", // M-S1-34
  SCAN_ILLISIBLE: "Ce code-barre n'est pas valide (chiffre de contrôle incorrect). Scannez à nouveau la boîte.", // M-S2-05 (same sentence)
  SCAN_ECHEC: "La recherche du code-barre n'a pas abouti. Réessayez ou recherchez par nom.",
  SCAN_INCREMENT: (n, lot) =>
    lot
      ? `Même produit que la ligne ${n} (lot ${lot}) : Qté +1. Saisissez un autre N° lot pour créer une nouvelle ligne.`
      : `Même produit que la ligne ${n} : Qté +1. Saisissez un N° lot différent pour créer une nouvelle ligne.`, // §4.7 mode « Scan increment »
  TTC_NON_SAISI: "Saisissez le total TTC de la facture fournisseur (Remplir totaux — F8).", // M-S1-40
  FOURNISSEUR_REQUIS: "Choisissez le fournisseur.", // M-S1-42
  NUMERO_REQUIS: "Saisissez le N° de facture fournisseur.", // M-S1-43
  A_COMPLETER: (n) => (n > 1 ? `${n} lignes importées sont à compléter.` : "1 ligne importée est à compléter."), // M-S1-41
  LIGNE_REQUISE: "Ajoutez au moins une ligne.", // M-S1-44
  VALIDATION_LONGUE: "La validation prend plus de temps que prévu. Vérification de l'état de la facture…", // M-S1-50
  VALIDATION_ECHEC: (raison) => `La validation n'a pas abouti : ${raison}. Rien n'a été enregistré en stock.`, // M-S1-51
  VALIDEE: (numero, n) => `Facture ${numero} validée — ${n} unités entrées en stock.`, // M-S1-52
  BROUILLON_ENREGISTRE: "Brouillon enregistré.", // M-S1-53
  ENREGISTREMENT_ECHEC: "Échec de l'enregistrement — Réessayer", // M-S1-54
  SUPPRIMER_BROUILLON: (numero) => `Supprimer le brouillon ${numero} ?`, // M-S1-55 (question)
  SUPPRIMER_BROUILLON_DEFINITIF: "Cette action est définitive.", // M-S1-55 (consequence)
  FACTURE_VALIDEE_REDIRECTION: "Cette facture est validée : utilisez « Modifier la facture » sur sa fiche.", // M-S1-60
});

/**
 * D5 « Valider la facture » (ux/02 §4.16): the summary, the règlement choice absorbed from the
 * former ReglementModal, the busy / timeout / failure states. Amounts arrive formatted.
 */
export const VALIDATION_S1 = Object.freeze({
  // « PHARMALLIANCE SPA · 3 lignes · 37 unités entrent en stock (Magasin principal) »
  resume: (fournisseur, n, unites, magasin) =>
    [
      fournisseur,
      `${n} ligne${n > 1 ? "s" : ""}`,
      `${unites} unité${unites > 1 ? "s" : ""} entre${unites > 1 ? "nt" : ""} en stock${magasin ? ` (${magasin})` : ""}`,
    ]
      .filter(Boolean)
      .join(" · "),
  ttcFournisseur: "Total TTC de la facture fournisseur",
  ttcCalcule: "Total TTC calculé",
  ecartAvoir: (ecart, tolerance) =>
    `Écart ${ecart} (tolérance ${tolerance}) : la facture sera validée et marquée « avoir financier à créer ».`,
  ecartInferieur: (ecart) =>
    `Écart ${ecart} : le total du fournisseur est inférieur au total calculé. Vérifiez la saisie ; la facture peut être validée.`,
  ecartTolere: (ecart, tolerance) => `Écart ${ecart}, dans la tolérance (${tolerance}).`,
  conforme: "Les totaux de la facture fournisseur et les totaux calculés sont identiques.",
  manquants: (unites, lignes) =>
    `${unites} unité${unites > 1 ? "s" : ""} manquante${unites > 1 ? "s" : ""} sur ${lignes} ligne${lignes > 1 ? "s" : ""} : un avoir produits pourra être créé.`,
  psychotropes: "Facture de psychotropes : le N° de lot de chaque ligne est contrôlé à la validation.",
  reglement: "Règlement",
  montantRegle: "Montant déjà réglé",
  resteDu: "Reste dû après validation",
  revenir: "Revenir",
  valider: "Valider la facture",
  reessayer: "Réessayer",
  enCours: "Validation en cours…",
});

/**
 * « Supprimer le brouillon » (ux/02 §4.17, M-S1-55): the action bar's danger button (WP-64, D-26 —
 * its label is the ACHAT_SAISIE binding's) and its confirmation.
 */
export const BROUILLON_S1 = Object.freeze({
  supprimer: "Supprimer le brouillon",
  supprimerConfirmer: "Supprimer",
  supprimerAnnuler: "Annuler",
  supprime: (numero) => `Brouillon ${numero} supprimé.`,
  suppressionEchec: "La suppression du brouillon n'a pas abouti.",
});

/** Field labels of the B1 header panel (ux/02 §4.5) and of the B3 entry band (§4.7), verbatim. */
export const LIBELLES_S1 = Object.freeze({
  titreNouveau: "Nouvel achat",
  titreBrouillon: (numero) => `Brouillon ${numero}`,
  enTete: "En-tête de la facture",
  enTeteBouton: "En-tête",
  fournisseur: "Fournisseur",
  nouveauFournisseur: "Nouveau fournisseur",
  numeroFacture: "N° facture fournisseur",
  dateFacture: "Date facture",
  dateReception: "Date réception",
  magasin: "Magasin",
  typeAchat: "Type d'achat",
  notes: "Notes",
  remplirTotaux: "Remplir totaux…",
  produit: "Produit — nom, DCI, labo ou code-barre",
  produitCourt: "Produit",
  quantite: "Qté payée",
  // Refonte WP-63 (D-25, A-74) — the label of « Qté » in « Qté payée, UG incluses ».
  quantiteIncluses: "Qté (UG incluses)",
  ug: "UG",
  ugPct: "UG %",
  numeroLot: "N° lot",
  peremption: "Péremption",
  puHt: "PU HT",
  ristourne: "Ristourne",
  tva: "TVA %",
  ppa: "PPA",
  // Refonte WP-69 (D-27) — a non-médicament line: « Prix de vente » (the stored prix_vente_ppa) and its margin.
  prixVente: "Prix de vente",
  marge: "Marge %",
  shp: "SHP",
  recue: "Qté reçue",
  // Refonte WP-63 (D-25) — the derived Qté reçue says its rule; typed only with the owner flag.
  recueLiee: "Qté reçue = Qté payée + UG",
  recueLieeIncluses: "Qté reçue = Qté (UG incluses)",
  recueDelier: "Saisir une Qté reçue différente",
  recueLier: "Recalculer la Qté reçue",
  recueLectureSeule: "Calculée selon Paramètres › Achats et étiquettes",
  tr: "T.R",
  ajouter: "Ajouter",
  mettreAJour: "Mettre à jour",
  modeNouvelle: "Nouvelle ligne",
  modeCompletion: (n) => `Lignes importées à compléter : ${n} restante${n > 1 ? "s" : ""} · Échap pour arrêter`,
  ouvrirFiche: "Ouvrir la fiche produit",
  modeModification: (n, nom) => `Modification de la ligne ${n} — ${nom} · Échap pour annuler`,
  facturePsychotropes: "Facture de psychotropes",
});

/**
 * Refonte WP-66 (M-29; D-29, CP §15.5, §15.8) — « Magasin de la ligne »: the selector next to T.R in the
 * entry band (Alt+N), its dialog, the band's note, the lines table's mark and the D5 split. The facture's
 * magasin stays the default of every other line; the server's refusals are said verbatim.
 */
export const DESTINATION_S1 = Object.freeze({
  titre: "Magasin et emplacement de la ligne",
  sousTitre: (produit) => (produit ? `${produit} — cette ligne seulement` : "Cette ligne seulement"),
  magasin: "Magasin",
  magasinFacture: (nom) => (nom ? `Magasin de la facture — ${nom}` : "Magasin de la facture"),
  principal: (nom) => `${nom} (principal)`,
  magasinSansNom: "Magasin",
  emplacement: "Emplacement (facultatif)",
  emplacementChoisir: "Aucun — à ranger plus tard",
  emplacementVide: "Aucun emplacement dans ce magasin",
  emplacementSansMagasin: "Choisissez d'abord le magasin.",
  aide: "Les autres lignes restent dans le magasin de la facture. Un emplacement appartient à son magasin.",
  appliquer: "Appliquer",
  annuler: "Annuler",
  chargement: "Chargement des magasins…",
  erreur: "Les magasins n'ont pas pu être chargés.",
  reessayer: "Réessayer",
  verrouillee: "Le magasin d'une ligne validée ne se modifie pas : ses unités sont en stock — faites un transfert de stock.",
  bouton: (texte) => `Magasin de la ligne : ${texte}`,
  note: (libelle, facture) => `${libelle} — cette ligne seulement${facture ? ` (facture : ${facture})` : ""}`,
  badgeAide: (libelle, facture) => `Reçue dans ${libelle}${facture ? ` — le magasin de la facture est ${facture}` : ""}`,
  partMagasin: (nom, unites) => `${nom ?? "magasin de la facture"} ${unites}`,
});

/** The product picker of the entry band (ux/02 §4.7 « Product picker behaviour »). */
export const PICKER_S1 = Object.freeze({
  placeholder: "Nom, DCI, laboratoire ou code-barre",
  groupeCatalogue: "Votre catalogue",
  groupeNomenclature: "Nomenclature nationale — à créer",
  stock: (n) => `Stock ${n}`,
  aucun: (q) => `Aucun produit ne correspond à « ${q} »`,
  aide: "Nom commercial, DCI, laboratoire, forme, dosage, N° ENR ou code-barre — dans n'importe quel ordre.",
  creer: (q) => `Créer le produit « ${q} »`,
  creerCodeBarre: (code) => `Créer un produit avec le code-barre ${code}`,
  numeroEnr: (n) => `N° ENR ${n}`,
  aChoisir: "Choisissez un produit (↑ ↓ puis Entrée)",
});

/** The B2 fournisseur strip and card (ux/02 §4.6). */
export const FOURNISSEUR_S1 = Object.freeze({
  choisir: "Choisissez un fournisseur pour voir son solde.",
  calcul: "Calcul du solde…",
  indisponible: "Solde indisponible",
  reessayer: "Réessayer",
  aPayer: (montant) => `Solde comptable : ${montant} à payer`,
  credit: (montant) => `Crédit en votre faveur : ${montant}`,
  aJour: "Solde comptable : à jour",
  // Trésorerie TWP-56 (refonte D-32 supersedes D-12, TO-11): the INSTANCE avoirs, outside the solde
  creditsEnAttente: (montant) => `Crédits en attente (non déduits) : ${montant}`,
  activite: (n, date) => (date ? `${n} factures · dernier achat le ${date}` : `${n} factures`),
  chargementListe: "Chargement des fournisseurs…",
  listeIndisponible: "Liste des fournisseurs indisponible.",
  placeholder: "Nom ou téléphone du fournisseur",
  secours: "Secours",
  manquants: "Manquants",
  carte: "Fournisseur",
});

/** The lines table (ux/02 §4.8). */
export const LIGNES_S1 = Object.freeze({
  titre: "Lignes",
  grille: "Lignes de la facture",
  colonnes: Object.freeze({
    produit: "Produit",
    lot: "N° lot",
    peremption: "Pér.",
    lotPeremption: "Lot · Pér.",
    quantite: "Qté",
    recue: "Reçue",
    puHt: "PU HT",
    ppa: "PPA",
    prixVente: "Prix de vente", // WP-69 — a non-médicament facture
    tva: "TVA",
    totalHt: "Total HT",
    marge: "Marge",
  }),
  modifier: "Modifier la ligne",
  supprimer: "Supprimer la ligne",
  ouvrirFiche: "Ouvrir la fiche produit",
  supprimerTitre: (n, nom) => `Supprimer la ligne ${n} — ${nom} ?`,
  supprimerConfirmer: "Supprimer",
  ugTooltip: (n) => (n > 1 ? `${n} unités gratuites (UG)` : "1 unité gratuite (UG)"),
  // Refonte WP-63 (A-74) — « UG incluses »: the Qté already holds them.
  ugInclusTooltip: (n) => (n > 1 ? `dont ${n} unités gratuites (UG)` : "dont 1 unité gratuite (UG)"),
  ugInclus: (n) => `dont ${n} UG`,
  manquantTooltip: (n) =>
    n > 1
      ? `${n} unités manquantes — un avoir produits pourra être créé`
      : "1 unité manquante — un avoir produits pourra être créé",
  sansPeremption: "Sans pér.",
  vide: "Aucune ligne. Recherchez un produit ou scannez une boîte (F2).",
  pied: (n, stock, manquant) =>
    [
      `${n} ligne${n > 1 ? "s" : ""}`,
      `${stock} unité${stock > 1 ? "s" : ""} entre${stock > 1 ? "nt" : ""} en stock`,
      ...(manquant > 0 ? [`${manquant} unité${manquant > 1 ? "s" : ""} manquante${manquant > 1 ? "s" : ""}`] : []),
    ].join(" · "),
});

/** The preview line under the entry band (ux/02 §4.7). Amounts arrive formatted. */
export const APERCU_S1 = Object.freeze({
  ligne: (montant) => `Ligne ${montant}`,
  ligneRistourne: (montant, brut, rist) => `Ligne ${montant} (${brut} − ${rist} ristourne)`,
  // Refonte WP-63 — the units entering stock: the paid units received + the UG (D-08 storage, A-74).
  stock: (total, payees, ug) => `Stock +${total} (${payees} payée${payees > 1 ? "s" : ""} + ${ug} UG)`,
  manquant: (n) => `Manquant ${n}`,
  marge: (pct) => `Marge ${pct}`,
  chifa: (montant) => `CHIFA théorique +${montant}/u`,
  fefo: "Inversion FEFO : un lot de ce produit avec une péremption plus lointaine est déjà en stock.",
});

/** The totals band (ux/02 §4.9) and the ux/02 §2.3 écart states. */
export const TOTAUX_S1 = Object.freeze({
  htBrut: "HT brut",
  ristournes: "Rist.",
  totalHt: "Total HT",
  remise: "Remise",
  htRemise: "HT remisé",
  tva: "TVA",
  ttc: "TTC",
  declare: "Déclaré",
  ppa: "PPA",
  shp: "SHP",
  marge: "Marge",
  detail: "Détail",
  nonSaisi: "Total TTC fournisseur non saisi",
  conforme: "Conforme",
  avoirFinancier: (montant) => `Écart ${montant} — avoir financier à créer`,
  inferieur: (montant) => `Écart ${montant} — total fournisseur inférieur, vérifiez la saisie`,
});

/** The F7 detail panel (ux/02 §4.9) and the F8 dialog D1 « Remplir totaux » (§4.10). */
export const DETAIL_S1 = Object.freeze({
  titre: "Détail des totaux",
  fermer: "Fermer le détail (F7 ou Échap)",
  calcul: "Calcul",
  rapprochement: "Rapprochement avec la facture fournisseur",
  ristournes: "Ristournes",
  remiseGlobale: "Remise globale",
  uniteRemise: "Unité de la remise globale",
  tvaTaux: (taux, base) => `TVA ${taux} % (base ${base})`,
  margePrev: "Marge prévisionnelle",
  total: "Total",
  calcule: "Calculé",
  ecart: "Écart",
  avoirFinancier: (montant, tolerance) =>
    `Avoir financier à créer : ${montant} (écart TTC > tolérance ${tolerance})`,
  modifierDeclares: "Modifier les totaux déclarés",
  libellesDeclares: Object.freeze({
    ht: "Total H.T",
    ht_remise: "Total H.T remisé",
    tva: "Total T.V.A",
    ppa: "Total PPA",
    shp: "Total SHP",
    ttc: "Total TTC",
  }),
  d1Titre: "Totaux de la facture fournisseur",
  d1Intro:
    "Recopiez les totaux imprimés sur la facture du fournisseur. Ils seront comparés aux totaux calculés à partir des lignes.",
  effacer: "Effacer les totaux",
  annuler: "Annuler",
  enregistrer: "Enregistrer",
  aucunTotal: "Aucun total saisi",
  totauxSaisis: (n, total, ttc) => (ttc ? `${n} / ${total} totaux saisis · TTC ${ttc}` : `${n} / ${total} totaux saisis`),
});

/** States of the page (ux/02 §4.18). */
export const ETATS_S1 = Object.freeze({
  chargementBrouillon: "Chargement du brouillon…",
  erreurBrouillon: (numero) => (numero ? `Impossible de charger le brouillon ${numero}.` : "Impossible de charger le brouillon."),
  retourJournal: "Retour au journal des achats",
  chargementMagasins: "Chargement des magasins…",
  magasinsIndisponibles: "Magasins indisponibles.",
  reessayer: "Réessayer",
  horsLigne:
    "Connexion perdue — votre saisie est conservée sur ce poste. L'enregistrement et la validation reprendront au retour de la connexion.",
  nonEnregistre: "Nouveau — non enregistré",
  enregistrement: "Enregistrement…",
  enregistreA: (heure) => `Brouillon enregistré à ${heure}`,
  modifie: "Modifications non enregistrées",
  // D7 — a local copy found on /achats/nouveau (ux/02 §4.17)
  copieTitre: "Brouillon local trouvé",
  copieTrouvee: (date, heure, fournisseur, n) =>
    `Un brouillon non enregistré du ${date} à ${heure} a été trouvé (${[fournisseur, `${n} ligne${n > 1 ? "s" : ""}`].filter(Boolean).join(", ")}).`,
  reprendre: "Reprendre",
  supprimerCopie: "Supprimer ce brouillon local",
  plusTard: "Plus tard",
  // D8 — leaving with unsaved changes (ux/02 §4.17)
  quitterTitre: "Enregistrer les modifications avant de quitter ?",
  quitterMessage: "Les lignes et l'en-tête saisis depuis le dernier enregistrement seront perdus si vous quittez sans enregistrer.",
  enregistrerQuitter: "Enregistrer et quitter",
  quitterSans: "Quitter sans enregistrer",
  rester: "Rester",
  navigationBloquee: "Enregistrez le brouillon (Ctrl+S) ou quittez par le menu pour changer d'écran.",
  validationEnCours: "Validation en cours…",
  horsLigneAction: "Hors connexion : l'enregistrement et la validation reprendront au retour de la connexion.",
});

/** The page itself: the « Importer » menu, the validation pre-checks (D5), the round-trip toasts. */
export const PAGE_S1 = Object.freeze({
  importer: "Importer",
  ocrFacture: "OCR facture",
  ocrVignette: "OCR vignette",
  efacture: "E-facture",
  bonReception: "Créer le bon de réception",
  efactureBandeau: (numero) =>
    `E-facture ${numero} importée : ses lignes suivent le document du fournisseur et ne se modifient pas ici.`,
  efactureImportee: (numero) => `E-facture ${numero} importée.`,
  bonReceptionCree: "Bon de réception créé.",
  produitCree: (nom) => `« ${nom} » créé et sélectionné.`,
  produitCreeAilleurs: (nom) => `« ${nom} » créé — la saisie a changé entre-temps, retrouvez-le par la recherche.`,
  ficheMiseAJour: (nom) => `Fiche « ${nom} » mise à jour — lignes actualisées.`,
  fournisseurCree: (nom) => `Fournisseur « ${nom} » créé et sélectionné.`,
  aCompleter: (n) => (n > 1 ? `${n} lignes à compléter, une par une, depuis la facture papier.` : "1 ligne à compléter depuis la facture papier."),
  dejaPresentes: "Ces lignes sont déjà sur la facture.",
  ocrTotaux: "Les totaux imprimés ont été lus : vérifiez-les puis enregistrez-les.",
  validerTitre: (numero) => (numero ? `Valider la facture ${numero}` : "Valider la facture"),
  precontroles: "Avant de valider, complétez :",
  ligneErreur: (n, nom, message) => `Ligne ${n} — ${nom} : ${message}`,
  corriger: "Corriger",
  fermer: "Fermer",
  numeroInterne: (n) => `#${String(n).padStart(5, "0")}`,
  secoursRattaches: (n) =>
    `${n} unité${n > 1 ? "s" : ""} de secours rattachée${n > 1 ? "s" : ""} : les lots entrent en stock déduction faite.`,
});

/** The import modes re-hosted in place of the entry band (ux/02 §4.14). */
export const IMPORT_S1 = Object.freeze({
  retour: "Retour à la saisie manuelle",
  ocrTitre: "OCR facture",
  ocrIntro:
    "Photographiez ou déposez la facture du fournisseur. Ses lignes seront proposées une par une dans la saisie, à vérifier et compléter avant d'être ajoutées.",
  ocrAnalyse: "Lecture de la facture…",
  ocrGlisser: "Déposez ici la photo ou le PDF de la facture",
  ocrAucuneLigne: "Aucune ligne détectée sur la facture.",
  ocrEchec: "La lecture de la facture n'a pas abouti.",
  choisirFichier: "Choisir un fichier",
  camera: "Prendre une photo",
  formats: "Image (JPG, PNG) ou PDF.",
  efTitre: "E-facture",
  efIntro: "Importez la facture électronique envoyée par un fournisseur connecté : par fournisseur, par QR code ou par son code à 8 caractères.",
  efRecherche: "Par fournisseur",
  efQr: "QR code",
  efCode: "Code",
  efFournisseur: "Fournisseur",
  efChercher: "Rechercher",
  efChoisirFournisseur: "Choisissez d'abord un fournisseur.",
  efQrLabel: "Contenu du QR code",
  efCodeLabel: "Code de la e-facture (8 caractères)",
  efImporter: "Importer",
  efImporterCette: "Importer cette e-facture",
  efCodeInvalide: "Le code comporte 8 caractères.",
  efEchec: "L'import de la e-facture n'a pas abouti.",
  efAucune: "Aucune e-facture reçue de ce fournisseur.",
  efDisponibles: (n) => (n > 1 ? `${n} e-factures reçues` : `${n} e-facture reçue`),
  efLignes: (n) => `${n} ligne${n > 1 ? "s" : ""}`,
});

/** D2 « Type de produit différent de l'achat » (ux/02 §4.11) and D4 « Ce code-barre correspond à … » (§4.15). */
export const TYPES_S1 = Object.freeze({
  d2Titre: "Type de produit différent de l'achat",
  achatDeType: (type) => `Cet achat est de type « ${type} ».`,
  produitDeType: (nom, type) => `Le produit « ${nom} » est de type « ${type} ».`,
  passerMixte: "Passer l'achat en Mixte",
  passerMixteAide: "L'achat devient « Mixte » (plusieurs types de produits).",
  ajouterQuandMeme: "Ajouter quand même",
  ajouterQuandMemeAide: (typeAchat, typeProduit) =>
    `L'achat reste « ${typeAchat} » ; la ligne garde le type « ${typeProduit} ».`,
  annulerEchap: "Annuler — Échap",
  // Variant (c) — a change of the Type d'achat that leaves lines of other types.
  lignesAutreType: (n, type) =>
    n > 1 ? `${n} lignes ne sont pas de type « ${type} ».` : `1 ligne n'est pas de type « ${type} ».`,
  garderType: (type) => `Garder « ${type} » (les lignes gardent leur type)`,
  annuler: "Annuler",
  d4Titre: (n) => `Ce code-barre correspond à ${n} produits`,
  d4Liste: "Produits portant ce code-barre",
  d4Stock: (n) => `Stock ${n}`,
  d4Corriger: "Corriger les codes-barres dans les fiches produit",
  choisir: "Choisir",
});

/** S2 « Créer le produit » from the achat (ux/02 §5, messages M-S2-nn of §5.7 verbatim). */
export const S2 = Object.freeze({
  titre: "Nouveau produit",
  sousTitre: (numero) =>
    numero ? `Il sera ajouté à la saisie de la facture ${numero}.` : "Il sera ajouté à la saisie de la facture.",
  type: "Type de produit",
  identification: "Identification",
  codesTitre: "Codes-barres de la boîte",
  prixTitre: "Prix et réglementation",
  nomCommercial: "Nom commercial",
  dci: "DCI",
  dosage: "Dosage",
  contenance: "Contenance",
  forme: "Forme",
  conditionnement: "Conditionnement",
  laboratoire: "Laboratoire",
  marque: "Marque",
  description: "Description",
  compteur: (n, max) => `${n} / ${max}`,
  numEnr: "N° ENR (CNAS)",
  codeBarre: "Code-barre de la boîte",
  codeBarreAide: "Scannez la boîte ou saisissez le code. Ce n'est pas le code du lot.",
  codeBarrePlaceholder: "Scannez la boîte",
  ajouterCode: "Ajouter un autre code-barre",
  retirerCode: "Retirer ce code-barre",
  principal: "Principal",
  ppa: "PPA",
  tva: "TVA",
  tvaChoisir: "Choisir…",
  stockMinimal: "Stock minimal (QTE MIN)",
  classification: "Classification",
  shp: (v) => `SHP ${v}`,
  psychotrope: "Psychotrope",
  // The existing confirmation of the fiche produit (pages/produit/ProduitForm.jsx, ux/02 §5.3).
  psychotropeTitre: "Marquer comme psychotrope ?",
  psychotropeConfirmation:
    "Ce produit sera traité comme une substance contrôlée : numéro de lot obligatoire à la validation des achats, fenêtre ordonnancier obligatoire à la vente (médecin, patient, n° d'ordonnance) et inscription automatique au registre des psychotropes. Confirmez-vous ?",
  psychotropeConfirmer: "Confirmer",
  PSYCHOTROPE_LISTE: "Un médicament psychotrope doit être classé Liste I ou Tableau B",
  remboursable: "Remboursable CNAS",
  tarifRef: "T.R (tarif de référence)",
  origine: "Origine",
  locale: "Locale",
  importee: "Importée",
  dermo: "Conseil dermo",
  dermoAide: "Classe ce produit pour le Dermo-Conseiller (facultatif) — modifiable depuis la fiche produit.",
  dermoRetirer: "Retirer",
  creerSuggestion: (q) => `Créer « ${q} »`,
  annuler: "Annuler — Échap",
  creer: "Créer le produit",
  creerRaccourci: "Ctrl+Entrée",
  creation: "Création…",
  verification: "Vérification…",
  // §5.5 — type mismatch inside the panel
  mismatch: (typeAchat, typeProduit) =>
    `Cet achat est de type « ${typeAchat} ». Ce produit sera de type « ${typeProduit} ».`,
  mismatchMixte: "Passer l'achat en Mixte",
  mismatchGarder: (typeAchat) => `Garder l'achat « ${typeAchat} » et ajouter la ligne quand même`,
  // §5.6 — Échap with typed data
  abandonTitre: "Abandonner la création de ce produit ?",
  abandonMessage: "Les informations saisies seront perdues.",
  abandonner: "Abandonner",
  continuer: "Continuer la saisie — Échap",
  // §5.4 — duplicate barcode warning
  doublonUtiliser: "Utiliser ce produit",
  doublonContinuer: "Continuer quand même",
  NOM_REQUIS: "Le nom commercial est obligatoire (2 caractères minimum).", // M-S2-01
  TVA_REQUISE: "Choisissez le taux de TVA de ce produit.", // M-S2-02
  TR_REQUIS: "Saisissez le tarif de référence (T.R) d'un produit remboursable.", // M-S2-03
  ORIGINE_REQUISE: "Indiquez si le produit est fabriqué localement ou importé.", // M-S2-04
  CODE_INVALIDE: "Ce code-barre n'est pas valide (chiffre de contrôle incorrect). Scannez à nouveau la boîte.", // M-S2-05
  MISMATCH_REQUIS: "Choisissez comment ajouter ce produit à l'achat.", // M-S2-06
  DESCRIPTION_TROP: (max) => `${max} caractères au maximum.`, // M-S2-07
  NOM_TROP: (max) => `${max} caractères au maximum.`,
  PPA_INVALIDE: (max) => `Saisissez un PPA entre 0,00 et ${max} DA.`,
  CODE_EN_DOUBLE: "Ce code-barre figure déjà dans la liste.",
  CODE_EST_LOT: "Ce code est une étiquette de lot Pharmakon, pas le code-barre de la boîte.",
  CODES_TROP: (max) => `${max} codes-barres au maximum par produit.`,
  CREE: (nom) => `Produit « ${nom} » créé et ajouté à la saisie.`, // M-S2-10
  CREE_DEJA: (nom) => `Le produit « ${nom} » avait bien été créé ; il est ajouté à la saisie.`, // M-S2-11
  ECHEC_RESEAU:
    "La création n'a pas abouti (connexion lente ou interrompue). Vos informations sont conservées : réessayez.", // M-S2-12
  ECHEC: (raison) => `La création n'a pas abouti : ${raison}`,
  doublonCode: (nom, type) => `Ce code-barre est déjà utilisé par « ${nom} » (${type}).`, // §5.4
});
